import React, { useEffect, useRef, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, ButtonBase, Stack, TextField, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { FONT_STACK_SUISSE_WORKS } from '~/components/brand/brandTokens';

import type { LensIntent, LensScopeBlockPayload } from '~/types/leData';

import { RICHARD_MICROCOPY } from './microcopy';

/** What the reader chose, kept so the card can show it while askRichard works on the answer. */
type Answer = { labels: Array<string>; text: string; delegated: boolean };

function RadioMark({ on, tokens }: { on: boolean; tokens: ReturnType<typeof lensTokens> }) {
  return (
    <Box
      aria-hidden
      component="span"
      sx={{
        border: `1.5px solid ${on ? tokens.accent : tokens.axis}`,
        borderRadius: '50%',
        display: 'inline-block',
        flex: 'none',
        height: 16,
        position: 'relative',
        width: 16,
        '&::after': on
          ? {
              backgroundColor: tokens.coralAccent,
              border: `1.5px solid ${tokens.accent}`,
              borderRadius: '50%',
              content: '""',
              inset: 3,
              position: 'absolute',
            }
          : undefined,
      }}
    />
  );
}

/**
 * One reader question from the planner, as a stacked list of answer rows: the recommended row
 * leads and is marked, "Something else" is its own row that opens a text field, and the only
 * footer action is to let askRichard take the recommendation. A single-select row answers on
 * click. Once answered, the list collapses to the chosen row so the thread does not carry a
 * greyed-out question while askRichard plans.
 */
export default function LensQuestionCards({
  answeredText,
  answeredQuestionAnswer,
  interactive,
  onSendTurn,
  payload,
}: {
  /** The reader's visible answer while the turn is in flight, for when local state did not survive. */
  answeredText?: string;
  answeredQuestionAnswer?: Extract<LensIntent, { kind: 'answer' }>['questionAnswer'];
  interactive: boolean;
  onSendTurn: (question: string, intent: LensIntent) => void;
  payload: LensScopeBlockPayload;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const [selected, setSelected] = useState<Array<string>>([]);
  const [text, setText] = useState('');
  const [showText, setShowText] = useState(false);
  const [answerLocal, setAnswer] = useState<Answer | null>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLInputElement>(null);
  const focusRecommended = interactive && payload.question?.selection === 'single';
  const recommendedId = payload.question?.recommendedOptionId;
  // The option group (not an option) takes focus on mount, so Enter picks the recommended option
  // without a second focus ring on the row.
  useEffect(() => {
    if (focusRecommended && recommendedId) {
      groupRef.current?.focus();
    }
  }, [focusRecommended, recommendedId]);
  useEffect(() => {
    if (showText) {
      textRef.current?.focus();
    }
  }, [showText]);
  // A new turn makes the question live again; the collapsed answer belongs to the old one.
  useEffect(() => {
    if (interactive) {
      setAnswer(null);
    }
  }, [interactive]);

  const question = payload.question;
  if (!question) {
    return null;
  }
  const persistedAnswer = answeredQuestionAnswer?.questionId === question.id ? answeredQuestionAnswer : undefined;
  const answeredLabels = question.options
    .filter((option) =>
      persistedAnswer
        ? persistedAnswer.optionIds?.includes(option.id)
        : option.label.trim() === answeredText?.trim(),
    )
    .map((option) => option.label);
  const answer: Answer | null =
    answerLocal ??
    (answeredText && !interactive
      ? answeredText.trim() === RICHARD_MICROCOPY.letRichardDecide
        ? { labels: [], text: '', delegated: true }
        : {
            labels: answeredLabels,
            text: persistedAnswer ? (persistedAnswer.text ?? '') : answeredLabels.length ? '' : answeredText.trim(),
            delegated: false,
          }
      : null);
  const recommendedOptionId = question.options.some((option) => option.id === question.recommendedOptionId)
    ? question.recommendedOptionId
    : undefined;
  const send = (optionIds: Array<string>, answerText = '', delegated = false) => {
    if (!interactive || (!optionIds.length && !answerText.trim())) {
      return;
    }
    const labels = question.options.filter((option) => optionIds.includes(option.id)).map((option) => option.label);
    const visibleText = delegated
      ? RICHARD_MICROCOPY.letRichardDecide
      : [...labels, answerText.trim()].filter(Boolean).join(', ');
    setAnswer({ labels, text: answerText.trim(), delegated });
    onSendTurn(visibleText, {
      kind: 'answer',
      brief: payload.brief,
      questionAnswer: {
        questionId: question.id,
        ...(optionIds.length ? { optionIds } : {}),
        ...(answerText.trim() ? { text: answerText.trim() } : {}),
        ...(delegated ? { delegated: true } : {}),
      },
    });
  };
  const hasOptions = question.options.length > 0;
  const multi = question.selection === 'multi';
  const fieldVisible = question.allowFreeText && (!hasOptions || showText);
  const submitText = () => send(multi ? selected : [], text);
  const rowSx = {
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${tokens.grid}`,
    borderRadius: 3,
    columnGap: 1.5,
    display: 'grid',
    gridTemplateColumns: '16px minmax(0, 1fr) auto',
    px: 2,
    py: 1.5,
    textAlign: 'left',
    transition: 'border-color 120ms, background-color 120ms',
    width: '100%',
  } as const;
  const optionRowSx = { gridTemplateColumns: 'minmax(0, 1fr) auto' } as const;

  const prompt = (
    <Typography
      component="h3"
      id={`question-${question.id}`}
      sx={{
        color: 'text.primary',
        fontFamily: FONT_STACK_SUISSE_WORKS,
        fontSize: 22,
        fontWeight: 400,
        letterSpacing: '-0.015em',
        lineHeight: 1.25,
      }}
    >
      {question.prompt}
    </Typography>
  );

  // Answered and waiting: only what was chosen, in the same row shape it was chosen from.
  if (!interactive && answer) {
    const chosen = answer.delegated
      ? (question.options.find((option) => option.id === recommendedOptionId)?.label ??
        RICHARD_MICROCOPY.letRichardDecide)
      : [...answer.labels, answer.text].filter(Boolean).join(', ');
    const detail = answer.delegated ? 'Left to askRichard. It took the recommendation.' : null;
    const alternatives = answerLocal || persistedAnswer?.optionIds ? question.options : [];
    return (
      <Stack aria-labelledby={`question-${question.id}`} role={multi ? 'group' : 'radiogroup'} spacing={1.75}>
        {prompt}
        <Box
          aria-checked="true"
          aria-label="Your answer"
          role={multi ? 'checkbox' : 'radio'}
          sx={{ ...rowSx, borderColor: tokens.grid }}
        >
          <Box
            aria-hidden
            sx={{
              alignItems: 'center',
              backgroundColor: tokens.accent,
              borderRadius: '50%',
              color: tokens.accentContrast,
              display: 'inline-flex',
              height: 18,
              justifyContent: 'center',
              width: 18,
            }}
          >
            <CheckIcon sx={{ fontSize: 12 }} />
          </Box>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{chosen}</Typography>
            {detail ? <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{detail}</Typography> : null}
          </Stack>
          <span />
        </Box>
        {alternatives
          .filter((option) => !answer.labels.includes(option.label) && option.label !== chosen)
          .map((option) => (
            <Box
              aria-checked="false"
              aria-disabled="true"
              key={option.id}
              role={multi ? 'checkbox' : 'radio'}
              sx={{ ...rowSx, opacity: 0.5 }}
            >
              <span />
              <Typography sx={{ fontSize: 15 }}>{option.label}</Typography>
              <span />
            </Box>
          ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={1.75}>
      {prompt}
      {hasOptions && (
        <Stack
          aria-labelledby={`question-${question.id}`}
          onKeyDown={(event) => {
            if (multi) {
              return;
            }
            const radios = Array.from(
              groupRef.current?.querySelectorAll<HTMLElement>('[role="radio"]:not(:disabled)') ?? [],
            );
            if (event.target === event.currentTarget) {
              if ((event.key === 'Enter' || event.key === ' ') && recommendedOptionId) {
                event.preventDefault();
                setSelected([recommendedOptionId]);
                send([recommendedOptionId]);
              } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                radios[0]?.focus();
              }
              return;
            }
            const step = ['ArrowRight', 'ArrowDown'].includes(event.key)
              ? 1
              : ['ArrowLeft', 'ArrowUp'].includes(event.key)
                ? -1
                : 0;
            const index = radios.indexOf(document.activeElement as HTMLElement);
            if (!step || index < 0) {
              return;
            }
            event.preventDefault();
            radios[(index + step + radios.length) % radios.length]?.focus();
          }}
          ref={groupRef}
          role={multi ? 'group' : 'radiogroup'}
          spacing={1}
          sx={{ outline: 'none', '&:focus-visible': { outline: 'none' } }}
          tabIndex={multi ? undefined : 0}
        >
          {question.options.map((option) => {
            // Checked only once chosen: the recommended row is a suggestion, marked by its border
            // and tag, so the radio's look and its aria-checked state agree.
            const on = selected.includes(option.id);
            return (
              <ButtonBase
                key={option.id}
                aria-checked={selected.includes(option.id)}
                disabled={!interactive}
                onClick={() => {
                  if (!multi) {
                    setSelected([option.id]);
                    send([option.id]);
                  } else {
                    setSelected((current) =>
                      current.includes(option.id) ? current.filter((id) => id !== option.id) : [...current, option.id],
                    );
                  }
                }}
                data-recommended={option.id === recommendedOptionId ? 'true' : undefined}
                role={multi ? 'checkbox' : 'radio'}
                sx={{
                  ...rowSx,
                  ...(multi ? {} : optionRowSx),
                  border: on ? `1.5px solid ${tokens.accent}` : `1px solid ${tokens.grid}`,
                  '&:hover': {
                    backgroundColor: alpha(tokens.accent, 0.04),
                    borderColor: on ? tokens.accent : alpha(theme.palette.text.primary, 0.4),
                  },
                  '&.Mui-disabled': { opacity: 0.65 },
                  '&.Mui-focusVisible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
                }}
              >
                {multi ? <RadioMark on={on} tokens={tokens} /> : null}
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{option.label}</Typography>
                  {option.description ? (
                    <Typography sx={{ color: 'text.secondary', fontSize: 13, lineHeight: 1.45 }}>
                      {option.description}
                    </Typography>
                  ) : null}
                </Stack>
                {option.id === recommendedOptionId ? (
                  <Typography
                    component="span"
                    sx={{
                      color: tokens.accent,
                      fontSize: 10.5,
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {RICHARD_MICROCOPY.recommendedBadge}
                  </Typography>
                ) : (
                  <span />
                )}
              </ButtonBase>
            );
          })}
          {question.allowFreeText && !showText ? (
            <ButtonBase
              disabled={!interactive}
              onClick={() => setShowText(true)}
              sx={{
                ...rowSx,
                ...(multi ? {} : optionRowSx),
                border: `1px dashed ${tokens.axis}`,
                color: 'text.secondary',
                '&:hover': { borderColor: tokens.accent, color: 'text.primary' },
                '&.Mui-focusVisible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
              }}
            >
              {multi ? <RadioMark on={false} tokens={tokens} /> : null}
              <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{RICHARD_MICROCOPY.somethingElse}…</Typography>
              <span />
            </ButtonBase>
          ) : null}
        </Stack>
      )}
      {fieldVisible && (
        <Stack alignItems="center" direction="row" spacing={1}>
          <TextField
            disabled={!interactive}
            fullWidth
            inputRef={textRef}
            label={question.selection === 'text' ? 'Your answer' : 'Or write your answer'}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                event.preventDefault();
                submitText();
              }
            }}
            size="small"
            slotProps={{ htmlInput: { style: { fontSize: 13 } } }}
            value={text}
          />
          <Button
            disabled={!interactive || !text.trim()}
            onClick={submitText}
            sx={{ fontSize: 13, fontWeight: 600, minWidth: 0, p: 0, textTransform: 'none' }}
            variant="text"
          >
            {RICHARD_MICROCOPY.sendAnswer}
          </Button>
        </Stack>
      )}
      {(multi || (hasOptions && recommendedOptionId)) && (
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
          {multi ? (
            <Button
              disabled={!interactive || (!text.trim() && !selected.length)}
              onClick={submitText}
              variant="contained"
            >
              Continue
            </Button>
          ) : (
            <span />
          )}
          {hasOptions && recommendedOptionId ? (
            <Button
              disabled={!interactive}
              onClick={() => send([recommendedOptionId], '', true)}
              size="small"
              sx={{
                color: tokens.accent,
                fontSize: 13,
                fontWeight: 600,
                minWidth: 0,
                p: 0,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
              }}
              variant="text"
            >
              {RICHARD_MICROCOPY.letRichardDecide}
            </Button>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
}
